/* ZENNT · Página de producto: variantes, packs, carrito lateral y sticky */
(() => {
  if (window.ZenntPdp) return;

  const formatMoney = (cents, format) => {
    const value = Number(cents) || 0;
    const fmt = format || '${{amount_no_decimals_with_comma_separator}}';
    const withDelimiters = (num, decimals, thousands, decimal) => {
      const fixed = (num / 100).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const intFmt = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? intFmt + decimal + dec : intFmt;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', ',');
        case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
        default: return withDelimiters(value, 0, '.', ',');
      }
    });
  };

  const escapeHtml = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const sizedImage = (url, width) => {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}width=${width}`;
  };

  class ZenntPdp {
    constructor(root) {
      this.root = root;
      this.cfg = JSON.parse(root.querySelector('[data-zp-config]').textContent);
      this.product = this.cfg.product;
      this.money = (c) => formatMoney(c, this.cfg.moneyFormat);
      this.routes = { root: (this.cfg.root || '/').replace(/\/?$/, '/') };
      this.form = root.querySelector('[data-zp-form]');
      this.idInput = root.querySelector('[data-zp-variant-id]');
      this.cta = root.querySelector('[data-zp-cta]');
      this.drawer = root.querySelector('[data-zp-drawer]');
      this.sticky = root.querySelector('[data-zp-sticky]');
      this.cart = null;
      this.ctaVisible = true;
      this.variant = this.product.variants.find((v) => String(v.id) === this.idInput.value) || this.product.variants[0];

      this.bindGallery();
      this.bindOptions();
      this.bindPacks();
      this.bindForm();
      this.bindDrawer();
      this.bindSticky();
      this.startCountdown();
      this.updateVariantUI();
      this.fetchCart().then(() => this.renderSticky()).catch(() => {});
    }

    /* ---------- Galería ---------- */
    bindGallery() {
      this.slides = this.root.querySelector('[data-zp-slides]');
      this.thumbs = [...this.root.querySelectorAll('[data-zp-thumb]')];
      this.thumbs.forEach((t) =>
        t.addEventListener('click', () => this.goToSlide(Number(t.dataset.zpThumb)))
      );
      if (!this.slides) return;
      let raf;
      this.slides.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
          this.thumbs.forEach((t, n) => t.classList.toggle('is-on', n === i));
        });
      });
    }

    goToSlide(i) {
      if (!this.slides) return;
      this.slides.scrollTo({ left: i * this.slides.clientWidth, behavior: 'smooth' });
    }

    /* ---------- Variantes ---------- */
    bindOptions() {
      this.optionSets = [...this.root.querySelectorAll('[data-zp-option]')];
      this.optionSets.forEach((set) =>
        set.addEventListener('change', () => {
          const selected = this.optionSets.map((s) => s.querySelector('input:checked')?.value);
          const match = this.product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
          set.querySelector('[data-zp-option-label]').textContent = set.querySelector('input:checked')?.value || '';
          if (match) {
            this.variant = match;
            this.idInput.value = match.id;
            this.updateVariantUI(true);
          } else {
            this.setCtaState(false, 'No disponible');
          }
        })
      );
    }

    updateVariantUI(changed = false) {
      const v = this.variant;
      const $ = (sel) => this.root.querySelector(sel);
      const compare = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : 0;

      $('[data-zp-price]').textContent = this.money(v.price);
      const cmp = $('[data-zp-compare]');
      const save = $('[data-zp-save]');
      cmp.hidden = !compare;
      save.hidden = !compare;
      if (compare) {
        cmp.textContent = this.money(compare);
        $('[data-zp-save-amount]').textContent = this.money(compare - v.price);
      }
      const badge = $('[data-zp-badge]');
      if (badge) {
        badge.hidden = !compare;
        if (compare) badge.textContent = badge.textContent.replace(/-\d+%/, `-${Math.floor(((compare - v.price) * 100) / compare)}%`);
      }
      const stickyPrice = $('[data-zp-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = this.money(v.price);

      // Packs
      const setPack = (key, price, comparePrice) => {
        const p = $(`[data-zp-pack-price="${key}"]`);
        const c = $(`[data-zp-pack-compare="${key}"]`);
        if (p) p.textContent = this.money(price);
        if (c) {
          c.textContent = this.money(comparePrice);
          c.hidden = !(comparePrice > price);
        }
      };
      const base = compare || v.price;
      setPack('1', v.price, base);
      setPack('2', Math.round((v.price * this.cfg.pack2Qty * (100 - this.cfg.pack2Discount)) / 100), base * this.cfg.pack2Qty);
      if (this.cfg.kitVariantId) {
        const kitFull = v.price * this.cfg.kitMainQty + this.cfg.kitVariantPrice * this.cfg.kitExtraQty;
        setPack(
          'kit',
          Math.round((kitFull * (100 - this.cfg.kitDiscount)) / 100),
          base * this.cfg.kitMainQty + (this.cfg.kitVariantCompare || this.cfg.kitVariantPrice) * this.cfg.kitExtraQty
        );
      }

      // Disponibilidad de valores
      this.optionSets.forEach((set, idx) => {
        set.querySelectorAll('.zp-val').forEach((label) => {
          const val = label.querySelector('input').value;
          const available = this.product.variants.some(
            (x) => x.available && x.options[idx] === val && x.options.every((o, i) => i === idx || o === v.options[i])
          );
          label.classList.toggle('is-unavailable', !available);
        });
      });

      this.setCtaState(v.available, v.available ? this.cfg.ctaText : 'Agotado');
      this.updateCtaPrice();

      if (changed) {
        if (v.featured_media) {
          const slides = [...this.root.querySelectorAll('.zp-slide')];
          const i = slides.findIndex((s) => s.dataset.mediaId === String(v.featured_media.id));
          if (i >= 0) this.goToSlide(i);
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    setCtaState(enabled, text) {
      this.cta.disabled = !enabled;
      this.root.querySelector('[data-zp-cta-text]').textContent = text;
      const stickyCta = this.root.querySelector('[data-zp-sticky-cta]');
      if (stickyCta) stickyCta.disabled = !enabled;
      if (!enabled) this.updateCtaPrice(true);
    }

    /* ---------- Packs ---------- */
    bindPacks() {
      this.root.querySelectorAll('input[name="zp-pack"]').forEach((r) =>
        r.addEventListener('change', () => this.updateCtaPrice())
      );
    }

    selectedPack() {
      return this.root.querySelector('input[name="zp-pack"]:checked')?.value || '1';
    }

    updateCtaPrice(clear = false) {
      const priceEl = this.root.querySelector('[data-zp-cta-price]');
      const sep = this.root.querySelector('[data-zp-cta-sep]');
      const packPrice = this.root.querySelector(`[data-zp-pack-price="${this.selectedPack()}"]`);
      const text = clear || this.cta.disabled ? '' : packPrice ? packPrice.textContent : this.money(this.variant.price);
      priceEl.textContent = text;
      sep.hidden = !text;
    }

    itemsForPack() {
      const id = Number(this.idInput.value);
      const pack = this.selectedPack();
      if (pack === '2') return [{ id, quantity: this.cfg.pack2Qty }];
      if (pack === 'kit' && this.cfg.kitVariantId)
        return [{ id, quantity: this.cfg.kitMainQty }, { id: this.cfg.kitVariantId, quantity: this.cfg.kitExtraQty }];
      return [{ id, quantity: 1 }];
    }

    /* ---------- Agregar al carrito ---------- */
    bindForm() {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.add(this.itemsForPack(), this.cta);
      });
    }

    horizonSectionIds() {
      return [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
    }

    async add(items, button) {
      const errorEl = this.root.querySelector('[data-zp-error]');
      errorEl.hidden = true;
      button?.classList.add('is-loading');
      try {
        const sections = this.horizonSectionIds();
        const res = await fetch(`${this.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, ...(sections.length ? { sections: sections.join(',') } : {}) }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'No se pudo agregar');
        await this.fetchCart();
        this.notifyTheme(data.sections);
        const first = (data.items || [])[0];
        const added = this.root.querySelector('[data-zp-added]');
        added.textContent = first ? `${this.cfg.addedText}: ${first.product_title}` : this.cfg.addedText;
        added.hidden = false;
        this.renderDrawer();
        this.openDrawer();
      } catch (err) {
        if (this.drawer.classList.contains('is-open')) {
          window.alert(err.message);
        } else {
          errorEl.textContent = err.message;
          errorEl.hidden = false;
        }
      } finally {
        button?.classList.remove('is-loading');
      }
    }

    async change(key, quantity) {
      const sections = this.horizonSectionIds();
      const res = await fetch(`${this.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, ...(sections.length ? { sections: sections.join(',') } : {}) }),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        window.alert(data.description || data.message || 'No se pudo actualizar');
        await this.fetchCart();
      } else {
        this.cart = data;
        this.notifyTheme(data.sections);
      }
      this.renderDrawer();
      this.renderSticky();
    }

    async fetchCart() {
      const res = await fetch(`${this.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
      this.cart = await res.json();
      return this.cart;
    }

    /* Mantiene sincronizado el ícono y el carrito propio del tema Horizon, sin abrir su carrito. */
    async notifyTheme(sections) {
      const cart = this.cart;
      try {
        const mod = await import('@shopify/events');
        const { CartLinesUpdateEvent } = mod;
        if (!CartLinesUpdateEvent) return;
        const deferred = CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(
          new CartLinesUpdateEvent({ action: 'update', context: 'product', lines: [], promise: deferred.promise })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: cart.item_count, source: 'zennt-pdp', sections: sections || undefined, didError: false },
        });
      } catch (e) {
        // Temas sin eventos estándar: actualiza contadores comunes.
        document.querySelectorAll('[data-cart-count], .cart-count-bubble span, .cart-count').forEach((el) => {
          el.textContent = cart.item_count;
        });
      }
    }

    /* ---------- Carrito lateral ---------- */
    bindDrawer() {
      this.drawer.querySelectorAll('[data-zp-close]').forEach((el) => el.addEventListener('click', () => this.closeDrawer()));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.closeDrawer();
      });

      this.drawer.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-zp-qty]');
        if (qtyBtn) {
          const line = this.cart.items.find((i) => i.key === qtyBtn.dataset.key);
          if (line) this.change(line.key, Math.max(0, line.quantity + Number(qtyBtn.dataset.zpQty)));
          return;
        }
        const rm = e.target.closest('[data-zp-remove]');
        if (rm) return this.change(rm.dataset.zpRemove, 0);
        const crossBtn = e.target.closest('[data-zp-cross-add]');
        if (crossBtn) return this.add([{ id: Number(crossBtn.dataset.zpCrossAdd), quantity: 1 }], crossBtn);
        const tierBtn = e.target.closest('[data-zp-tier-btn]');
        if (tierBtn && this.tierAction) return this.add([{ id: this.tierAction, quantity: 1 }], tierBtn);
      });

      // Selector de variante en las tarjetas del carrito
      this.drawer.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-zp-cross-sel]');
        if (!sel) return;
        const card = sel.closest('[data-zp-cross]');
        const opt = sel.selectedOptions[0];
        const btn = card.querySelector('[data-zp-cross-add]');
        btn.dataset.zpCrossAdd = sel.value;
        btn.disabled = opt.disabled;
        btn.textContent = opt.disabled ? 'Agotado' : '+ Agregar';
        const price = card.querySelector('[data-zp-cross-price]');
        if (price && opt.dataset.price) price.textContent = opt.dataset.price;
      });
    }

    openDrawer() {
      this.lastFocus = document.activeElement;
      this.drawer.hidden = false;
      document.documentElement.classList.add('zp-lock');
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        this.drawer.querySelector('.zp-drawer__panel').focus({ preventScroll: true });
      });
      this.renderSticky();
    }

    closeDrawer() {
      this.drawer.classList.remove('is-open');
      document.documentElement.classList.remove('zp-lock');
      this.root.querySelector('[data-zp-added]').hidden = true;
      setTimeout(() => {
        this.drawer.hidden = true;
        this.renderSticky();
      }, 300);
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    renderDrawer() {
      const cart = this.cart;
      if (!cart) return;
      const $ = (sel) => this.drawer.querySelector(sel);
      $('[data-zp-count]').textContent = cart.item_count;

      // Líneas
      $('[data-zp-lines]').innerHTML = cart.items.length
        ? cart.items
            .map((item) => {
              const compareUnit = this.cfg.compareAt[item.variant_id] || 0;
              const before = Math.max(compareUnit * item.quantity, item.original_line_price);
              const discounts = (item.discounts || []).map((d) => escapeHtml(d.title)).join(', ');
              const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
              return `
            <div class="zp-line">
              <a class="zp-line__img" href="${escapeHtml(item.url)}">${item.image ? `<img src="${escapeHtml(sizedImage(item.image, 160))}" alt="" loading="lazy">` : ''}</a>
              <div class="zp-line__info">
                <b>${escapeHtml(item.product_title)}</b>
                ${variantTitle ? `<small>${escapeHtml(variantTitle)}</small>` : ''}
                ${discounts ? `<span class="zp-line__disc">${discounts}</span>` : ''}
                <div class="zp-qty">
                  <button type="button" data-zp-qty="-1" data-key="${escapeHtml(item.key)}" aria-label="Quitar uno">−</button>
                  <span>${item.quantity}</span>
                  <button type="button" data-zp-qty="1" data-key="${escapeHtml(item.key)}" aria-label="Agregar uno">+</button>
                </div>
              </div>
              <div class="zp-line__price">
                ${this.money(item.final_line_price)}
                ${before > item.final_line_price ? `<s>${this.money(before)}</s>` : ''}
                <button type="button" class="zp-line__rm" data-zp-remove="${escapeHtml(item.key)}">Eliminar</button>
              </div>
            </div>`;
            })
            .join('')
        : '<p style="padding:24px 16px;color:#6b6b6b">Tu carrito está vacío.</p>';

      this.renderTiers();

      // Complementarios. En modo combo se muestran todos los productos de la oferta (se puede sumar otro igual u otro color).
      const dxs = $('[data-zp-dxs]');
      const combo = dxs && dxs.hasAttribute('data-zp-combo');
      const inCart = new Set(cart.items.map((i) => i.product_id));
      const crossCards = [...this.drawer.querySelectorAll('[data-zp-cross]')];
      const tierProduct = this.tierProduct;
      if (!combo) crossCards.forEach((c) => (c.hidden = inCart.has(Number(c.dataset.zpCross)) || Number(c.dataset.zpCross) === tierProduct));
      if (dxs) dxs.hidden = crossCards.every((c) => c.hidden) || cart.item_count === 0;

      // Totales y ahorro
      $('[data-zp-subtotal]').textContent = this.money(cart.total_price);
      const saved =
        cart.items.reduce((sum, i) => {
          const cmp = this.cfg.compareAt[i.variant_id] || 0;
          return sum + (cmp > i.original_price ? (cmp - i.original_price) * i.quantity : 0);
        }, 0) + (cart.total_discount || 0);
      const savedEl = $('[data-zp-saved]');
      savedEl.hidden = saved <= 0;
      savedEl.textContent = this.cfg.savedText.replace('[monto]', this.money(saved));
      this.drawer.querySelector('.zp-drawer__foot').hidden = cart.item_count === 0;
    }

    /* Metas de descuento por cantidad: 2 productos = meta 1, 3 productos = meta 2 */
    renderTiers() {
      const box = this.drawer.querySelector('[data-zp-tier]');
      if (!box) return;
      const cart = this.cart;
      const ids = new Set(this.cfg.tierEarplugIds || []);
      const units = cart.items.filter((i) => ids.has(i.product_id)).reduce((n, i) => n + i.quantity, 0);
      const { tier1Pct: t1, tier2Pct: t2 } = this.cfg;
      const $ = (sel) => box.querySelector(sel);
      box.hidden = cart.item_count === 0;

      const thisEligible = ids.has(this.product.id) && !this.cta.disabled;
      const sameAgain = thisEligible ? { id: Number(this.idInput.value), title: 'Suma otro igual', productId: this.product.id } : null;
      const kitInCart = cart.items.some((i) => i.product_id === this.cfg.kitProductId);
      const kitOffer =
        this.cfg.kitVariantId && this.cfg.kitAvailable && !kitInCart && ids.has(this.cfg.kitProductId)
          ? { id: this.cfg.kitVariantId, title: `Agrega ${this.cfg.kitTitle}`, productId: this.cfg.kitProductId }
          : null;

      let text;
      let level;
      let cta = null;
      if (units >= 3) {
        level = 2;
        text = `¡Listo! Tienes <b>${t2}% OFF</b> en tu pedido.`;
      } else if (units === 2) {
        level = 1;
        text = `¡<b>${t1}% OFF</b> activado! Suma 1 producto más y sube a <b>${t2}%</b>.`;
        cta = kitOffer || sameAgain;
        if (cta) cta.sub = `y lleva todo con ${t2}% OFF`;
      } else {
        level = 0;
        const missing = 2 - units;
        text = `Agrega <b>${missing} producto${missing > 1 ? 's' : ''} más</b> y obtén <b>${t1}% OFF</b> en todo.`;
        cta = sameAgain || kitOffer;
        if (cta) cta.sub = `o combina con otro modelo · ${t1}% OFF`;
      }
      // Con la lista de combo visible, la sugerencia única sobra
      if (this.drawer.querySelector('[data-zp-combo]')) cta = null;
      $('[data-zp-tier-text]').innerHTML = text;
      $('[data-zp-tier-bar]').style.width = `${[0, 25, 50, 100][Math.min(units, 3)]}%`;
      box.querySelectorAll('[data-zp-tier-step]').forEach((el) => el.classList.toggle('is-on', Number(el.dataset.zpTierStep) <= level));
      box.classList.toggle('is-done', level === 2);
      const ctaBox = $('[data-zp-tier-cta]');
      this.tierAction = cta ? cta.id : null;
      this.tierProduct = cta ? cta.productId : null;
      ctaBox.hidden = !cta;
      if (cta) {
        $('[data-zp-tier-cta-title]').textContent = cta.title;
        $('[data-zp-tier-cta-text]').textContent = cta.sub;
      }
    }

    /* ---------- Sticky / mini carrito ---------- */
    bindSticky() {
      if (!this.sticky) return;
      this.sticky.querySelector('[data-zp-sticky-cta]')?.addEventListener('click', (e) =>
        this.add(this.itemsForPack(), e.currentTarget)
      );
      this.sticky.querySelector('[data-zp-mini]')?.addEventListener('click', () => {
        this.renderDrawer();
        this.openDrawer();
      });
      const io = new IntersectionObserver(([entry]) => {
        this.ctaVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
        this.renderSticky();
      });
      io.observe(this.cta);
    }

    renderSticky() {
      if (!this.sticky) return;
      const hasItems = this.cart && this.cart.item_count > 0;
      const drawerOpen = this.drawer.classList.contains('is-open');
      const add = this.sticky.querySelector('[data-zp-sticky-add]');
      const mini = this.sticky.querySelector('[data-zp-mini]');

      add.hidden = hasItems || this.ctaVisible;
      mini.hidden = !hasItems;
      if (hasItems) {
        const first = this.cart.items[0];
        this.sticky.querySelector('[data-zp-mini-total]').textContent = this.money(this.cart.total_price);
        this.sticky.querySelector('[data-zp-mini-count]').textContent = this.cart.item_count;
        this.sticky.querySelector('[data-zp-mini-img]').innerHTML = first.image
          ? `<img src="${escapeHtml(sizedImage(first.image, 120))}" alt="">`
          : '';
      }
      const visible = !drawerOpen && (!add.hidden || !mini.hidden);
      this.sticky.hidden = !visible;
      document.body.classList.toggle('zp-has-sticky', visible);
    }

    /* ---------- Cuenta regresiva ---------- */
    startCountdown() {
      const end = Date.parse(this.root.dataset.countdownEnd || '');
      const els = [...this.root.querySelectorAll('[data-zp-countdown]')];
      const note = this.root.querySelector('[data-zp-mini-note]');
      const baseNote = note?.textContent || '';
      if (!end) return;
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const diff = end - Date.now();
        if (diff <= 0) {
          els.forEach((el) => (el.hidden = true));
          if (note) note.textContent = baseNote;
          clearInterval(this.timer);
          return;
        }
        const d = Math.floor(diff / 864e5);
        const h = Math.floor((diff % 864e5) / 36e5);
        const m = Math.floor((diff % 36e5) / 6e4);
        const s = Math.floor((diff % 6e4) / 1e3);
        const text = `${d > 0 ? pad(d) + 'd ' : ''}${pad(h)}:${pad(m)}:${pad(s)}`;
        els.forEach((el) => {
          el.textContent = text;
          el.hidden = false;
        });
        if (note) note.textContent = `${baseNote} · ${text}`;
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
  }

  window.ZenntPdp = ZenntPdp;
  const init = () =>
    document.querySelectorAll('[data-zp]').forEach((el) => {
      if (!el.zp) el.zp = new ZenntPdp(el);
    });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
